import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  useMidiMappingStore,
  formatBindingLabel,
} from '@/stores/midiMappingStore'
import { useHoldKey } from '@/hooks/useGlobalKeyCapture'

interface BadgePosition {
  id: string
  label: string
  text: string
  rect: DOMRect
}

/**
 * While Shift+M is held, floats a small badge next to every mappable
 * control that has a binding, listing its current MIDI mapping(s).
 * Strictly visual — non-interactive — so held-state doesn't block
 * normal clicks on the controls below.
 *
 * Follows the same RAF-refresh pattern as MidiLearnOverlay so badges
 * track their controls through scrolls and layout shifts while the
 * reveal gesture is active. Release Shift+M and the overlay tears
 * down; no per-frame cost when not revealing.
 */
export const MappedControlBadges: React.FC = () => {
  const revealing = useHoldKey({ key: 'm', shift: true, allowInInputs: true })
  const descriptors = useMidiMappingStore(s => s.descriptors)
  const bindings = useMidiMappingStore(s => s.bindings)
  const [badges, setBadges] = useState<BadgePosition[]>([])
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!revealing) {
      setBadges([])
      return
    }
    const tick = () => {
      const next: BadgePosition[] = []
      for (const descriptor of Object.values(descriptors)) {
        const controlBindings = bindings[descriptor.id]
        if (!controlBindings || controlBindings.length === 0) continue
        const el = descriptor.getElement?.()
        if (!el) continue
        const rect = el.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) continue
        next.push({
          id: descriptor.id,
          label: descriptor.label,
          text: controlBindings.map(formatBindingLabel).join(' · '),
          rect,
        })
      }
      setBadges(next)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [revealing, descriptors, bindings])

  if (!revealing || typeof document === 'undefined') return null

  return createPortal(
    <div
      aria-hidden
      className="fixed inset-0 pointer-events-none z-[9997]"
      style={{ contain: 'layout paint' }}
    >
      {badges.map(badge => (
        <div
          key={badge.id}
          className="absolute px-1.5 py-0.5 rounded-sm bg-accent text-[color:var(--accent-ink)] text-[10px] font-mono shadow-md whitespace-nowrap"
          style={{
            left: badge.rect.left,
            // Position above the control with a small gap; if the
            // control is near the viewport top, drop below instead.
            top:
              badge.rect.top > 24 ? badge.rect.top - 18 : badge.rect.bottom + 2,
          }}
        >
          {badge.text}
        </div>
      ))}
    </div>,
    document.body,
  )
}
